/**
 * filterUpgradePackages — which subscription packages the Upgrade popup offers.
 *
 * WHY THIS FILE EXISTS (QA in-app-marketing pass, 2026-08-01)
 * ----------------------------------------------------------
 * `UpgradePopup.jsx` carried TWO hand-written copies of this rule — one for the
 * plan cards (was: `!required || sub_tier >= required || is_active`) and one for
 * the Compare-Plans table (was: `!required || sub_tier >= required || sub_tier == 0`).
 * The two tails disagree: `is_active` vs `sub_tier === 0`.
 *
 * Today they happen to agree on live staging data only because the Free package
 * (sub_tier 0) is the single `is_active: true` row. The moment any paid package is
 * marked active — Essential (sub_tier 1) is the obvious candidate, it is priced and
 * carries a Stripe product id — the plan cards would offer a package the comparison
 * table silently omits. Two sources of truth for one rule is the defect; this module
 * is the one source.
 *
 * THE RULE: an upgrade popup opened for a required tier offers that tier and every
 * tier above it, plus the Free tier as the "what you have now" reference column.
 * With no required tier, nothing is filtered out.
 *
 * The Free tier is identified by `sub_tier === 0`, not by `is_active`. `is_active`
 * describes whether a package is purchasable at all — an orthogonal concern that
 * must not decide which tiers an upgrade path displays.
 */

/** The Free tier's `sub_tier`. Free is always shown as the baseline column. */
export const FREE_SUB_TIER = 0;

/**
 * @param {Array<{sub_tier: number}>} packages   packages from `/subscription/packages/`
 * @param {number|null|undefined} requiredSubscription  the tier the upsell demands
 * @returns {Array} the packages the popup should render, original order preserved
 */
export const filterUpgradePackages = (packages, requiredSubscription) => {
  if (!Array.isArray(packages)) return [];
  if (!requiredSubscription) return packages;

  return packages.filter(
    (pkg) =>
      pkg.sub_tier >= requiredSubscription || pkg.sub_tier === FREE_SUB_TIER
  );
};

export default filterUpgradePackages;
